import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers3, ChevronDown } from 'lucide-react';
import { usePluginStore } from '@/stores/plugin-store';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { useLayerVisibilityStore } from '@/stores/layer-visibility-store';
import {
  buildActiveMapLayers,
  groupLayersByCategory,
  CATEGORY_LABELS,
  type ActiveMapLayer,
} from './plugin-layer-manager';
import { LayerCategoryFilter } from './layer-category-filter';
import { cn } from '@/lib/utils';

/** Fixed category display order (operational first, reference last). */
const CATEGORY_ORDER = ['operational', 'threat', 'environment', 'reference'] as const;

/** Collapsible HUD for toggling feed track layers, top-right of the map. */
export function TrackLayerSwitcher(): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const plugins = usePluginStore((s) => s.activePlugins);
  const visible = useLayerVisibilityStore((s) => s.visible);

  const layers = buildActiveMapLayers(plugins);
  if (layers.length === 0) return null;

  const activeCount = layers.filter((layer) => visible.get(layer.fullId)).length;
  const groups = groupLayersByCategory(layers);

  return (
    <div className="flex justify-end">
      <AnimatePresence mode="wait" initial={false}>
        {!open ? (
          <motion.button
            key="chip"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.14 }}
            style={{ transformOrigin: 'top right' }}
            className={cn(
              'group flex items-center gap-2 rounded-lg border bg-echo-grey/90 px-2.5 py-2 shadow-lg backdrop-blur transition-colors',
              activeCount > 0 ? 'border-echo-yellow/50' : 'border-echo-border hover:border-echo-border/80',
            )}
          >
            <Layers3
              className={cn('h-4 w-4', activeCount > 0 ? 'text-echo-yellow' : 'text-muted-foreground')}
            />
            <span className="font-chakra text-xs font-semibold uppercase tracking-wider text-foreground">
              Layers
            </span>
            {activeCount > 0 && (
              <span className="rounded bg-echo-yellow/20 px-1 font-mono text-[10px] text-echo-yellow">
                {activeCount}
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-y-0.5" />
          </motion.button>
        ) : (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
            style={{ transformOrigin: 'top right' }}
            className="w-60 overflow-hidden rounded-lg border border-echo-border bg-echo-grey/92 text-xs text-foreground shadow-2xl ring-1 ring-black/20 backdrop-blur"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-2.5 transition-colors hover:bg-echo-border/25"
            >
              <Layers3 className="h-4 w-4 text-echo-yellow" />
              <span className="flex-1 text-left font-chakra text-xs font-semibold uppercase tracking-[0.18em] text-foreground">
                Feed Layers
              </span>
              <ChevronDown className="h-3.5 w-3.5 rotate-180 text-muted-foreground" />
            </button>
            <div className="h-px bg-gradient-to-r from-transparent via-echo-border to-transparent" />
            <div className="max-h-[60vh] space-y-2 overflow-y-auto p-2">
              {CATEGORY_ORDER.filter((category) => groups.has(category)).map((category) => (
                <CategoryGroup key={category} category={category} layers={groups.get(category) ?? []} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** A titled group of layer rows for one category. */
function CategoryGroup({
  category,
  layers,
}: {
  category: string;
  layers: ActiveMapLayer[];
}): React.JSX.Element {
  return (
    <div>
      <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {CATEGORY_LABELS.get(category) ?? category}
      </div>
      <div className="space-y-0.5">
        {layers.map((layer) => (
          <LayerRow key={layer.fullId} layer={layer} />
        ))}
      </div>
    </div>
  );
}

/** A single layer: the toggle row plus, when visible, its type-filter dropdown. */
function LayerRow({ layer }: { layer: ActiveMapLayer }): React.JSX.Element {
  const visible = useLayerVisibilityStore((s) => s.visible.get(layer.fullId) ?? false);
  const toggle = useLayerVisibilityStore((s) => s.toggle);
  const count = usePluginDataStore((s) => s.data.get(layer.fullId)?.length);

  return (
    <div className={cn('rounded-md', visible && 'bg-echo-yellow/10')}>
      <button
        type="button"
        onClick={() => toggle(layer.fullId)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2 py-1.5 transition-colors',
          !visible && 'hover:bg-echo-border/30',
        )}
      >
        <HealthDot count={count} />
        <span className={cn('flex-1 truncate text-left', visible ? 'text-foreground' : 'text-muted-foreground')}>
          {layer.definition.label}
        </span>
        {count !== undefined && (
          <span className="font-mono text-[10px] text-muted-foreground">{count}</span>
        )}
        <Switch on={visible} />
      </button>
      {visible && <LayerCategoryFilter layer={layer} />}
    </div>
  );
}

/** Feed health indicator: grey idle, amber connected/empty, green receiving. */
function HealthDot({ count }: { count: number | undefined }): React.JSX.Element {
  const color =
    count === undefined ? 'bg-muted-foreground/50' : count > 0 ? 'bg-emerald-400' : 'bg-amber-400';
  return <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', color)} />;
}

/** Slim on/off switch matching the tactical HUD. */
function Switch({ on }: { on: boolean }): React.JSX.Element {
  return (
    <span
      className={cn(
        'relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full transition-colors',
        on ? 'bg-echo-yellow' : 'bg-echo-border',
      )}
    >
      <span
        className={cn(
          'absolute h-2.5 w-2.5 rounded-full bg-white shadow-sm transition-transform',
          on ? 'translate-x-3' : 'translate-x-0.5',
        )}
      />
    </span>
  );
}
